import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updatePatient } from '../../../Redux/PatientSlice';
import { toast } from 'react-toastify';
import { ColorRing } from 'react-loader-spinner';

const UpdatePatientModal = ({ isOpen, onClose, patientToEdit }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [patientInfo, setPatientInfo] = useState({
        firstName: '',
        lastName: '',
        dob: '',
        gender: '',
        address: '',
        phone: '',
        idType: '',
        idNumber: ''
    });

    useEffect(() => {
        if (patientToEdit) {
            // Format the date to YYYY-MM-DD for the date input
            const formattedDate = patientToEdit.date_of_birth
                ? new Date(patientToEdit.date_of_birth).toLocaleDateString()
                : '';

            setPatientInfo({
                firstName: patientToEdit.first_name || '',
                lastName: patientToEdit.last_name || '',
                dob: formattedDate,
                gender: patientToEdit.gender || '',
                address: patientToEdit.address || '',
                phone: patientToEdit.phone_number || '',
                idType: patientToEdit.id_type || '',
                idNumber: patientToEdit.id_number || ''
            });
        }
    }, [patientToEdit]);

    const handlePatientInfoChange = (field, value) => {
        setPatientInfo(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const patientData = {
                first_name: patientInfo.firstName,
                last_name: patientInfo.lastName,
                date_of_birth: patientInfo.dob,
                gender: patientInfo.gender,
                phone_number: patientInfo.phone,
                id_number: patientInfo.idNumber,
                id_type: patientInfo.idType,
                address: patientInfo.address
            };

            await dispatch(updatePatient({
                patientId: patientToEdit.id,
                patientData
            })).unwrap();

            toast.success('Patient updated successfully');
            onClose();
        } catch (error) {
            toast.error('Failed to update patient');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !patientToEdit) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">Update Patient</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                value={patientInfo.firstName}
                                onChange={(e) => handlePatientInfoChange('firstName', e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                value={patientInfo.lastName}
                                onChange={(e) => handlePatientInfoChange('lastName', e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="dob" className="block text-sm font-medium">Date of Birth</label>
                            <input
                                type="date"
                                id="dob"
                                value={patientInfo.dob}
                                onChange={(e) => handlePatientInfoChange('dob', e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="gender" className="block text-sm font-medium">Gender</label>
                            <select
                                id="gender"
                                className="w-full p-2 border rounded"
                                value={patientInfo.gender}
                                onChange={(e) => handlePatientInfoChange('gender', e.target.value)}
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="address" className="block text-sm font-medium">Address</label>
                            <input
                                type="text"
                                id="address"
                                value={patientInfo.address}
                                onChange={(e) => handlePatientInfoChange('address', e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium">Phone</label>
                            <input
                                type="tel"
                                id="phone"
                                value={patientInfo.phone}
                                onChange={(e) => handlePatientInfoChange('phone', e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="idType" className="block text-sm font-medium">ID Type</label>
                            <select
                                id="idType"
                                className="w-full p-2 border rounded"
                                value={patientInfo.idType}
                                onChange={(e) => handlePatientInfoChange('idType', e.target.value)}
                                required
                            >
                                <option value="">Select ID type</option>
                                <option value="Ghana Card">Ghana Card</option>
                                <option value="Health Insuarance">Health Insurance</option>
                                <option value="Passport">Passport</option>
                                <option value="Driver's license">Driver's license</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="idNumber" className="block text-sm font-medium">ID Number</label>
                            <input
                                type="text"
                                id="idNumber"
                                value={patientInfo.idNumber}
                                onChange={(e) => handlePatientInfoChange('idNumber', e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                required
                            />
                        </div>
                    </div>

                    <div className="flex justify-end mt-6 space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-md hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex items-center px-4 py-2 text-white bg-green-500 rounded-md hover:bg-green-600"
                            disabled={loading}
                        >
                            {loading ? (
                                <ColorRing
                                    visible={true}
                                    height="20"
                                    width="20"
                                    ariaLabel="loading"
                                    wrapperStyle={{}}
                                    wrapperClass="mr-2"
                                    colors={['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff']}
                                />
                            ) : null}
                            Update
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdatePatientModal; 